import { Link, useLoaderData, useRevalidator } from 'react-router';
import { useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import StatusBadge from '@/components/StatusBadge';
import { formatDate } from '@/lib/utils';
import supabase from '@/utils/supabase';
import { getClientStatusDisplay } from '@/utils/status';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type Project = {
  id: string;
  project_name: string;
  status: string;
  due_date: string | null;
  deliverables: { id: string; is_client_ingesting: boolean }[];
  client_review_link: string | null;
};

export async function loader() {
  const { data: projects, error: projectsError } = await supabase
    .from('projects')
    .select(
      'id, project_name, status, due_date, deliverables(id, is_client_ingesting), client_review_link'
    )
    .neq('status', 'Cancelled')
    .neq('status', 'Completed')
    .order('created_at', { ascending: false });

  if (projectsError || !projects) {
    throw new Response('Failed to load projects', { status: 500 });
  }

  return projects;
}

function HomePage() {
  const revalidator = useRevalidator();

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') revalidator.revalidate();
    }, 10_000);
    return () => clearInterval(interval);
  }, [revalidator]);

  const projects = useLoaderData() as Project[];

  return (
    <div className="flex flex-col px-4">
      <Table className="text-base">
        <TableHeader>
          <TableRow>
            <TableHead className="w-40">Status</TableHead>
            <TableHead className="w-auto">Project</TableHead>
            <TableHead className="w-80">Delivery Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {projects.map((project) => {
            const anyIngesting = project.deliverables.some(
              (d) => d.is_client_ingesting
            );
            const status = anyIngesting ? 'processing' : project.status;
            const { label, colour, pulse } = getClientStatusDisplay(status);

            return (
              <TableRow key={project.id}>
                <TableCell>
                  <StatusBadge label={label} colour={colour} pulse={pulse} />
                </TableCell>
                <TableCell>
                  <Link to={`projects/${project.id}`}>
                    {project.project_name}
                  </Link>
                  <Badge
                    variant={'outline'}
                    className="bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-200 ml-4"
                  >
                    {project.deliverables.length}{' '}
                    {project.deliverables.length === 1 ? 'video' : 'videos'}
                  </Badge>
                  {project.client_review_link ? (
                    <Badge
                      variant={'outline'}
                      className="bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800 dark:bg-green-800 dark:text-green-200 dark:hover:bg-green-700 dark:hover:text-green-100 ml-4"
                      asChild
                    >
                      <a
                        href={project.client_review_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Review
                      </a>
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell>
                  {project.due_date ? formatDate(project.due_date) : '-'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default HomePage;
